import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ShieldCheck, Smartphone } from "lucide-react";

/** Second login step: verify TOTP code, or enroll (QR) when 2FA is mandatory. */
export default function MfaStep({ mode, mfaToken, onSuccess, onCancel }) {
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (mode !== "setup") return;
    api.post("/auth/mfa/setup", { mfa_token: mfaToken })
      .then(({ data }) => setSetup(data))
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail)));
  }, [mode, mfaToken]);

  async function submit(e) {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const url = mode === "setup" ? "/auth/mfa/enable" : "/auth/mfa/verify";
      const { data } = await api.post(url, { mfa_token: mfaToken, code: code.trim() });
      onSuccess(data.user);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  }

  return (
    <form onSubmit={submit} className="space-y-5" data-testid={`mfa-${mode}-form`}>
      <div className="flex items-start gap-3 p-4 rounded-xl bg-viva-50 border border-viva-200">
        <ShieldCheck className="w-6 h-6 text-viva-600 shrink-0 mt-0.5" />
        <div className="text-sm text-slate-700">
          {mode === "setup" ? (
            <>
              <b>Autenticazione a due fattori obbligatoria</b> per il tuo ruolo.
              Scansiona il codice QR con Google Authenticator (o un&apos;app equivalente) e inserisci il codice a 6 cifre.
            </>
          ) : (
            <>Inserisci il codice a 6 cifre generato dalla tua app di autenticazione.</>
          )}
        </div>
      </div>
      {mode === "setup" && (
        <div className="flex flex-col items-center gap-2" data-testid="mfa-qr-block">
          {setup ? (
            <>
              <img src={`data:image/png;base64,${setup.qr_png_base64}`} alt="Codice QR 2FA" className="w-44 h-44 border rounded-lg" data-testid="mfa-qr-image" />
              <div className="text-[11px] text-slate-500 text-center">
                Non riesci a scansionare? Inserisci manualmente la chiave:
                <code className="block font-mono text-slate-800 mt-1 break-all" data-testid="mfa-secret">{setup.secret}</code>
              </div>
            </>
          ) : !error && <Loader2 className="w-6 h-6 animate-spin text-viva-500" />}
        </div>
      )}
      <div>
        <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
          <Smartphone className="w-3.5 h-3.5" /> Codice di verifica
        </Label>
        <Input
          autoFocus inputMode="numeric" maxLength={6} value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
          placeholder="123456" data-testid="mfa-code-input"
          className="mt-1.5 h-11 rounded-lg tracking-[0.4em] text-center font-mono text-lg"
        />
      </div>
      {error && (
        <div data-testid="mfa-error" className="rounded-lg bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm">{error}</div>
      )}
      <Button type="submit" disabled={busy || code.length !== 6} data-testid="mfa-submit-button"
              className="w-full h-11 rounded-lg bg-viva-500 hover:bg-viva-600 text-white font-semibold">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : mode === "setup" ? "Attiva e accedi" : "Verifica e accedi"}
      </Button>
      <button type="button" onClick={onCancel} data-testid="mfa-cancel-button"
              className="w-full text-xs font-semibold text-slate-500 hover:text-slate-800">
        ← Torna al login
      </button>
      <p className="text-[11px] text-slate-400 text-center">
        Hai perso il dispositivo? Contatta l&apos;amministratore per azzerare l&apos;autenticazione a due fattori.
      </p>
    </form>
  );
}
